'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Popover, PopoverContent } from '@savvy-kit/components/popover';
import { Separator } from '@savvy-kit/components/separator';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { Tag } from '@savvy-kit/patterns/tag';
import { isNil } from 'es-toolkit';
import TrackedButton from 'src/components/tracked/tracked-button';

export type ColumnGroup<TDisplayOption extends string> = {
  readonly label: string;
  readonly options: readonly TDisplayOption[];
};

export type TableColumnsDisplayConfig<TColumn extends string, TDisplayOption extends string> = {
  // Column definitions
  readonly displayOptions: readonly TDisplayOption[];
  readonly displayOptionToColumns: (options: readonly TDisplayOption[]) => readonly TColumn[];
  readonly columnsToDisplayOptions: (columns: readonly TColumn[]) => readonly TDisplayOption[];

  // Optional column groups
  readonly groups?: readonly ColumnGroup<TDisplayOption>[];

  // Required columns (always included, not counted in limit)
  readonly requiredColumns: readonly TColumn[];
  readonly ensureRequiredColumns: (columns: readonly TColumn[]) => TColumn[];

  // Configuration
  readonly maxSelectable?: number;
  readonly maxTotalColumns?: number; // Total columns including required (for display)
  readonly title: string; // "Display columns" vs "Display properties"
  readonly countLabel: string; // "Columns" vs "Properties"
  readonly limitWarningMessage?: string;
  readonly includeRequiredInCount?: boolean; // Whether to include required columns in the displayed count

  // Defaults
  readonly getDefaultColumns: () => TColumn[];
  readonly getHardcodedDefaults: () => TColumn[]; // Hardcoded defaults (ignores localStorage)
  readonly setDefaultColumns: (columns: TColumn[]) => void;

  // URL sync (handled by parent)
  readonly selectedColumns: readonly TColumn[];
  readonly onColumnsChange: (columns: TColumn[]) => void;
};

type TableColumnsDisplayProps<TColumn extends string, TDisplayOption extends string> = {
  readonly open: boolean;
  readonly onClose: () => void;
  readonly anchorEl: HTMLElement | null;
  readonly config: TableColumnsDisplayConfig<TColumn, TDisplayOption>;
};

export function TableColumnsDisplay<TColumn extends string, TDisplayOption extends string>({
  open,
  onClose,
  anchorEl,
  config,
}: TableColumnsDisplayProps<TColumn, TDisplayOption>) {
  // Local state for pending changes - only applied when user clicks Apply
  const [pendingColumns, setPendingColumns] = useState<TColumn[]>(() => Array.from(config.selectedColumns));
  const [showLimitWarning, setShowLimitWarning] = useState(false);

  // Reset pending state when dialog opens or selectedColumns changes
  useEffect(() => {
    if (open) {
      setPendingColumns(Array.from(config.selectedColumns));
      setShowLimitWarning(false);
    }
  }, [open, config.selectedColumns]);

  // Filter out required columns to get only selectable display options
  const selectableColumns = useMemo(
    () => pendingColumns.filter((col) => !config.requiredColumns.includes(col)),
    [pendingColumns, config.requiredColumns],
  );

  const localSelectedOptions = useMemo(
    () => config.columnsToDisplayOptions(selectableColumns),
    [selectableColumns, config],
  );

  const requiredColumnsCount = config.requiredColumns.length;
  const displayCount = config.includeRequiredInCount
    ? requiredColumnsCount + localSelectedOptions.length
    : localSelectedOptions.length;
  const displayMax = (() => {
    if (!isNil(config.maxSelectable)) {
      if (config.includeRequiredInCount) {
        return config.maxTotalColumns;
      }

      return config.maxSelectable;
    }

    return undefined;
  })();

  const handleOptionToggle = useCallback(
    (option: TDisplayOption) => {
      const already = localSelectedOptions.includes(option);
      if (already) {
        setShowLimitWarning(false);
        const newOptions = localSelectedOptions.filter((o) => o !== option);
        const optionColumns = config.displayOptionToColumns(newOptions);
        const newColumns = config.ensureRequiredColumns(optionColumns);
        setPendingColumns(newColumns);
        return;
      }
      if (!isNil(config.maxSelectable) && localSelectedOptions.length >= config.maxSelectable) {
        setShowLimitWarning(true);
        return;
      }
      setShowLimitWarning(false);
      const newOptions = [...localSelectedOptions, option];
      const optionColumns = config.displayOptionToColumns(newOptions);
      const newColumns = config.ensureRequiredColumns(optionColumns);
      setPendingColumns(newColumns);
    },
    [localSelectedOptions, config],
  );

  const handleGroupToggle = useCallback(
    (group: ColumnGroup<TDisplayOption>) => {
      const allGroupOptionsSelected = group.options.every((opt) => localSelectedOptions.includes(opt));

      if (allGroupOptionsSelected) {
        setShowLimitWarning(false);
        const newOptions = localSelectedOptions.filter((o) => !group.options.includes(o));
        const optionColumns = config.displayOptionToColumns(newOptions);
        const newColumns = config.ensureRequiredColumns(optionColumns);
        setPendingColumns(newColumns);
        return;
      }

      const optionsToAdd = group.options.filter((opt) => !localSelectedOptions.includes(opt));
      const wouldExceedLimit =
        !isNil(config.maxSelectable) && localSelectedOptions.length + optionsToAdd.length > config.maxSelectable;

      if (wouldExceedLimit) {
        setShowLimitWarning(true);
        return;
      }

      setShowLimitWarning(false);
      const newOptions = [...localSelectedOptions, ...optionsToAdd];
      const optionColumns = config.displayOptionToColumns(newOptions);
      const newColumns = config.ensureRequiredColumns(optionColumns);
      setPendingColumns(newColumns);
    },
    [localSelectedOptions, config],
  );

  const handleApply = useCallback(() => {
    // Save preferences and update URL when user explicitly confirms
    config.setDefaultColumns([...pendingColumns]);
    config.onColumnsChange([...pendingColumns]);
    onClose();
  }, [pendingColumns, config, onClose]);

  const handleReset = useCallback(() => {
    const hardcodedDefaults = config.getHardcodedDefaults();
    const columnsWithRequired = config.ensureRequiredColumns([...hardcodedDefaults]);
    setPendingColumns(columnsWithRequired);
    setShowLimitWarning(false);
    // Reset also immediately applies and saves
    config.onColumnsChange([...columnsWithRequired]);
    config.setDefaultColumns([...columnsWithRequired]);
  }, [config]);

  const handleClose = useCallback(() => {
    // Reset pending changes when closing without applying
    setPendingColumns(Array.from(config.selectedColumns));
    onClose();
  }, [config.selectedColumns, onClose]);

  // Determine which options are in groups
  const groupedOptionsSet = useMemo(() => {
    const set = new Set<TDisplayOption>();
    if (config.groups) {
      for (const group of config.groups) {
        for (const opt of group.options) {
          set.add(opt);
        }
      }
    }
    return set;
  }, [config.groups]);

  // Ungrouped options that should be displayed individually
  const ungroupedOptions = useMemo(
    () => config.displayOptions.filter((opt) => !groupedOptionsSet.has(opt)),
    [config.displayOptions, groupedOptionsSet],
  );

  const renderOption = (option: TDisplayOption) => {
    const isSelected = localSelectedOptions.includes(option);

    return (
      <Tag
        key={String(option)}
        variant="selectable"
        onClick={() => handleOptionToggle(option)}
        className={cn('cursor-pointer', isSelected ? 'border-primary text-primary' : '')}
      >
        {option}
      </Tag>
    );
  };

  const renderGroup = (group: ColumnGroup<TDisplayOption>) => {
    const isSelected = group.options.every((opt) => localSelectedOptions.includes(opt));

    return (
      <Tag
        key={group.label}
        variant="selectable"
        onClick={() => handleGroupToggle(group)}
        className={cn('cursor-pointer', isSelected ? 'border-primary text-primary' : '')}
      >
        {group.label}
      </Tag>
    );
  };

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) {
          handleClose();
        }
      }}
    >
      {/* Restores the pre-migration theme.zIndex.modal + 1; at the default z-50 this sits under
          appBar (1200) and drawer (1100). */}
      <PopoverContent
        data-testid="table-columns-display-popover"
        anchor={anchorEl}
        side="bottom"
        align="end"
        sideOffset={8}
        className="w-[406px] p-0"
        positionerClassName="z-[1400]"
      >
        <div>
          <div className="flex flex-col gap-4 p-6">
            <div className="flex flex-row gap-0 items-center justify-between">
              <Text variant="subtitle-sm" className="font-semibold" render={<h6 />}>
                {config.title}
              </Text>
              <Text variant="body-sm" color={showLimitWarning ? 'destructive' : 'secondary'}>
                {!isNil(displayMax) ? `${displayCount}/${displayMax}` : displayCount} {config.countLabel}
              </Text>
            </div>
            {showLimitWarning && config.limitWarningMessage && (
              <Text variant="body-sm" color="destructive">
                {config.limitWarningMessage}
              </Text>
            )}
            <div>
              <div className="flex flex-row gap-2 flex-wrap">
                {ungroupedOptions.map(renderOption)}
                {config.groups?.map(renderGroup)}
              </div>
            </div>
          </div>
          <Separator className="bg-border-light" />
          <div className="flex flex-row items-center justify-end p-4 gap-2">
            <TrackedButton elementName="display-columns-reset-button" variant="ghost" onClick={handleReset}>
              Reset to default
            </TrackedButton>
            <TrackedButton elementName="display-columns-set-default-button" variant="ghost" onClick={handleApply}>
              Apply
            </TrackedButton>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
